import { createSelector } from "reselect";
import { createCachedSelector } from "re-reselect";

import { PERMISSION, PERMISSION_ROLE_TYPE, PERMISSION_TYPE } from "lib/constants";
import { validRolesForPermissionType } from "lib/permissions";
import rollbar from "lib/rollbar";
import { memoize } from "lib/util";
import { isEqualCreator } from "redux/utils";

export const getAbilities = createSelector(
    (state) => state.ws.user?.abilities,
    (abilities) => abilities || [],
);

const filterAccessByPermission = (positionAccess, permission) => {
    const isRetain = permission === PERMISSION.RETAIN;
    return positionAccess.filter((access) => (isRetain ? access[3] : access[2]));
};

// temporary backfill, if you find yourself using this, you probably want
// to be checking `state.ws.abilities` and `state.ws.accessiblePositions` instead
const permissionTypeToAbility = new Map([
    [PERMISSION_TYPE.NONE, null],
    [PERMISSION_TYPE.VIEW, "read.retain_data"],
    [PERMISSION_TYPE.EDIT, "update.retain_data"],
    [PERMISSION_TYPE.ADMIN, "update.retain_admin_data"],
]);

export const hasPermission = createSelector(
    (state) => state.ws.isAdmin,
    (state) => state.ws.adpAccessDisabled,
    (state) => state.ws.positionAccess,
    (state) => state.ws.permissionUser,
    (state) => state.ws.hirePermissionRole,
    (state) => state.ws.retainPermissionRole,
    (state) => state.ws.hireAllAccess,
    (state) => state.ws.retainAllAccess,
    getAbilities,
    (state) => state.ws.accessiblePositions,
    (
        isAdmin,
        adpAccessDisabled,
        positionAccess,
        principalUser,
        hirePermissionRole,
        retainPermissionRole,
        hireAllAccess,
        retainAllAccess,
        abilities,
        accessiblePositions,
    ) =>
        memoize(
            (
                permission,
                permissionType,
                { user = null, positionID = null, positionIDs = null },
            ) => {
                if (isAdmin) {
                    return true;
                }

                if (user && user === principalUser) {
                    return true;
                }

                const isRetain = permission === PERMISSION.RETAIN;

                if (isRetain) {
                    if (positionIDs) {
                        rollbar.warn("Retain auth check with position ids");
                    }

                    if (positionID && !accessiblePositions.includes(positionID)) {
                        return false;
                    }

                    return abilities.includes(permissionTypeToAbility.get(permissionType));
                }

                // Users who have been disabled by an ADP Connector Admin should not see Retain
                if (isRetain && adpAccessDisabled) return false;

                const validRoles = validRolesForPermissionType(permissionType);
                const permissionRole = isRetain ? retainPermissionRole : hirePermissionRole;
                const allAccess = isRetain ? retainAllAccess : hireAllAccess;

                // If we don't have a valid role return false
                if (!validRoles.includes(permissionRole)) return false;

                // Check if the user has all access
                if (allAccess) return true;

                // If not then check if they have access to provided position(s)
                const ids = (positionIDs || []).concat(positionID || []);
                if (ids.length > 0) {
                    const accessIds = new Set(
                        filterAccessByPermission(positionAccess, permission).map(([pid]) => pid),
                    );
                    return ids.some((pid) => accessIds.has(pid));
                }

                return true;
            },
        ),
);

const hasPermissionRole = ({ isAdmin, hirePermissionRole, abilities }) =>
    memoize((principalPermissionRole, permission = null) => {
        if (isAdmin) {
            return true;
        }

        const validRoles = new Set(
            validRolesForPermissionType(PERMISSION_ROLE_TYPE[principalPermissionRole]),
        );

        if (permission === PERMISSION.RETAIN) {
            return abilities.includes(
                permissionTypeToAbility.get(PERMISSION_ROLE_TYPE[principalPermissionRole]),
            );
        }

        if (permission === PERMISSION.HIRE) {
            return validRoles.has(hirePermissionRole);
        }

        // permission is null or something other than retain/hire so we check both
        // retain and hire
        return (
            abilities.includes(
                permissionTypeToAbility.get(PERMISSION_ROLE_TYPE[principalPermissionRole]),
            ) || validRoles.has(hirePermissionRole)
        );
    });

export const hasPermissionRoleSelector = createSelector(
    (state) => state.ws.isAdmin,
    (state) => state.ws.hirePermissionRole,
    (state) => state.ws.retainPermissionRole,
    (state) => state.ws.hireAllAccess,
    (state) => state.ws.retainAllAccess,
    getAbilities,
    (
        isAdmin,
        hirePermissionRole,
        retainPermissionRole,
        hireAllAccess,
        retainAllAccess,
        abilities,
    ) =>
        hasPermissionRole({
            isAdmin,
            hirePermissionRole,
            retainPermissionRole,
            hireAllAccess,
            retainAllAccess,
            abilities,
        }),
);

export const hasViewPermission = createSelector(
    (state, permission, options) => ({ state, permission, ...options }),
    ({ state, permission, user = null, positionID = null, positionIDs = null }) =>
        hasPermission(state)(permission, PERMISSION_TYPE.VIEW, { user, positionID, positionIDs }),
);

export const hasEditPermission = createSelector(
    (state, permission, options) => ({ state, permission, ...options }),
    ({ state, permission, user = null, positionID = null, positionIDs = null }) =>
        hasPermission(state)(permission, PERMISSION_TYPE.EDIT, { user, positionID, positionIDs }),
);

export const hasAdminPermission = createSelector(
    (state, permission, options) => ({ state, permission, ...options }),
    ({ state, permission, user = null, positionID = null, positionIDs = null }) =>
        hasPermission(state)(permission, PERMISSION_TYPE.ADMIN, { user, positionID, positionIDs }),
);

export const getCompanyName = createSelector(
    (state) => state.positions,
    (positions) => (positions && positions.length > 0 ? positions[0].company_name : ""),
);

// Example language: en-US
// See: https://datatracker.ietf.org/doc/html/rfc5646
export const getUserLanguage = createSelector(
    (state) => state.ws.language,
    (language) => {
        if (!language) {
            return null;
        }
        return language.split("-", 1)[0];
    },
);

const MAX_COMMENT_COUNT = 99;

export const formatCount = (c) => (c > MAX_COMMENT_COUNT ? `${MAX_COMMENT_COUNT}+` : `${c}`);

export const getCommentCounts = createSelector(
    (state) => state.ws.alertsCount,
    (counts) =>
        Object.entries(counts).reduce(
            (memo, [k, v]) => ({
                ...memo,
                [k]: v,
                [`${k}Display`]: formatCount(v),
            }),
            {},
        ),
);

export const hasAbility = createCachedSelector(
    getAbilities,
    (_, ability) => ability,
    (abilities, ability) => abilities.includes(ability),
)((state, ability) => ability || "");

export const hasAbilities = createCachedSelector(
    getAbilities,
    (_, abilities) => abilities,
    (userAbilities, abilities) => abilities.map((a) => userAbilities.includes(a)),
)({
    keySelector: (state, abilities) => JSON.stringify(abilities || []),
    selectorCreator: isEqualCreator,
});
